import { AxiosError } from 'axios';
import { ProxyAgentErrorBody } from '../interfaces/proxy-agent';

export const PROXY_AGENT_ERROR_CODES = {
  TUNNEL_NOT_DIALABLE: 'PROXY_AGENT_TUNNEL_NOT_DIALABLE',
  TMS_UNAVAILABLE: 'TMS_UNAVAILABLE'
} as const;

/** The typed error body of a response, or undefined when the response carries none. */
export function getProxyAgentErrorBody(error?: AxiosError): ProxyAgentErrorBody | undefined {
  const data = error?.response?.data;

  return data && typeof data === 'object' ? (data as ProxyAgentErrorBody) : undefined;
}

export function isProxyAgentTunnelError(body?: ProxyAgentErrorBody): boolean {
  return body?.error_code === PROXY_AGENT_ERROR_CODES.TUNNEL_NOT_DIALABLE || body?.error_code === PROXY_AGENT_ERROR_CODES.TMS_UNAVAILABLE;
}

const TUNNEL_HEALTH_AGENT_DOWN = 'AGENT_DOWN';
const TUNNEL_HEALTH_PROXY_DOWN = 'PROXY_DOWN';
const TUNNEL_HEALTH_AGENT_UNSUPPORTED = 'AGENT_UNSUPPORTED';

const TUNNEL_SERVICE_UNAVAILABLE =
  'Hevo could not reach its tunnel service right now, so this action could not be completed. Please try again in a few minutes.';

/**
 * Turns the typed 503 a source test or save gets when its tunnel is unusable into something the
 * customer can act on. Returns undefined for any other error so callers fall back to their usual
 * message. The raw `details` (tunnel id, TMS codes) are left out on purpose: they help support, not
 * the customer.
 */
export function getProxyAgentTunnelErrorMessage(body?: ProxyAgentErrorBody): string | undefined {
  if (!body || !isProxyAgentTunnelError(body)) {
    return undefined;
  }

  // Every proxy-agent call answers this when TMS is down (create, delete, details, source test), and
  // this is the shared error path, so the text must not name one of them.
  if (body.error_code === PROXY_AGENT_ERROR_CODES.TMS_UNAVAILABLE || body.details?.health === TUNNEL_HEALTH_PROXY_DOWN) {
    return TUNNEL_SERVICE_UNAVAILABLE;
  }

  // Whether it never connected or dropped after it was picked: the tunnel's state cannot tell the two
  // apart (a new address is always still pending), so one message covers both.
  if (body.details?.health === TUNNEL_HEALTH_AGENT_DOWN) {
    return (
      'The proxy agent selected for this source is not connected to Hevo. ' +
      'Make sure it is running and shows Connected under Settings > Team > Connectivity > Proxy Agents.'
    );
  }

  // Hevo refused the agent for its version, so being connected is not the problem: the agent has to be updated.
  if (body.details?.health === TUNNEL_HEALTH_AGENT_UNSUPPORTED) {
    return (
      'The proxy agent selected for this source is running a version Hevo no longer supports. ' +
      'Update the agent: pull the current agent image and restart it, then try again. ' +
      'You can see its version under Settings > Team > Connectivity > Proxy Agents.'
    );
  }

  // The agent is connected and Hermes has already waited for the tunnel, so whatever is left (the
  // database refused or timed out, the tunnel did not come up) is a failure to reach the database.
  return (
    'Hevo could not connect to your database through the proxy agent. ' +
    'Check the database host and port, and that the database is running and reachable from the machine where the agent runs.'
  );
}
